import { useState } from 'react';
import { Check, Pencil, RotateCcw, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { AssignmentRule } from '@app/shared';
import { useProperties } from '@/features/properties/queries';
import { useCategories } from '@/features/reference/queries';
import { apiErrorMessage } from '@/lib/api';
import { formatCents } from '@/lib/format';
import { Card } from '@/components/ui/card';
import { IconActionButton } from '@/components/ui/icon-action-button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { usePagination } from '@/hooks/use-pagination';
import { AssignmentTabs } from './AssignmentTabs';
import { useAssignmentRules, useDeleteAssignmentRule, useReplayAssignmentRule, useUpdateAssignmentRule } from './queries';

function RuleRow({ rule, propertyNames, categoryLabels }: {
  rule: AssignmentRule;
  propertyNames: Map<number, string>;
  categoryLabels: Map<number, string>;
}) {
  const [editing, setEditing] = useState(false);
  const [propertyId, setPropertyId] = useState(String(rule.propertyId));
  const [categoryId, setCategoryId] = useState(String(rule.categoryId));
  const [minAmount, setMinAmount] = useState(String(rule.minAmountCents / 100));
  const [maxAmount, setMaxAmount] = useState(String(rule.maxAmountCents / 100));

  const properties = useProperties();
  const categories = useCategories();
  const update = useUpdateAssignmentRule();
  const remove = useDeleteAssignmentRule();
  const replay = useReplayAssignmentRule();

  function save() {
    update.mutate(
      {
        id: rule.id,
        input: {
          propertyId: Number(propertyId),
          categoryId: Number(categoryId),
          minAmountCents: Math.round(Number(minAmount) * 100),
          maxAmountCents: Math.round(Number(maxAmount) * 100),
        },
      },
      {
        onSuccess: () => {
          toast.success('Règle mise à jour');
          setEditing(false);
        },
        onError: (error) => toast.error(apiErrorMessage(error, 'Échec de la mise à jour')),
      },
    );
  }

  function remove_() {
    remove.mutate(rule.id, {
      onSuccess: () => toast.success('Règle désactivée'),
      onError: (error) => toast.error(apiErrorMessage(error, 'Échec de la suppression')),
    });
  }

  function replayNow() {
    replay.mutate(rule.id, {
      onSuccess: (result) => toast.success(`${String(result.revalidated)} écriture(s) revalidée(s)`),
      onError: (error) => toast.error(apiErrorMessage(error, 'Échec de la relecture')),
    });
  }

  if (editing) {
    return (
      <TableRow>
        <TableCell className="max-w-xs truncate" title={rule.normalizedLabel}>
          {rule.normalizedLabel}
        </TableCell>
        <TableCell>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {properties.data?.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>
                  {property.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.data?.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell className="flex gap-1">
          <Input type="number" step="0.01" value={minAmount} onChange={(event) => setMinAmount(event.target.value)} className="w-24" />
          <Input type="number" step="0.01" value={maxAmount} onChange={(event) => setMaxAmount(event.target.value)} className="w-24" />
        </TableCell>
        <TableCell>{rule.timesApplied}</TableCell>
        <TableCell className="flex gap-1">
          <IconActionButton label="Enregistrer" onClick={save} disabled={update.isPending}>
            <Check />
          </IconActionButton>
          <IconActionButton label="Annuler" variant="ghost" onClick={() => setEditing(false)}>
            <X />
          </IconActionButton>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="max-w-xs truncate" title={rule.normalizedLabel}>
        {rule.normalizedLabel}
      </TableCell>
      <TableCell>{propertyNames.get(rule.propertyId) ?? rule.propertyId}</TableCell>
      <TableCell>{categoryLabels.get(rule.categoryId) ?? rule.categoryId}</TableCell>
      <TableCell>
        {formatCents(rule.minAmountCents, 'EUR')} – {formatCents(rule.maxAmountCents, 'EUR')}
      </TableCell>
      <TableCell>{rule.timesApplied}</TableCell>
      <TableCell className="flex flex-wrap gap-1">
        <IconActionButton label="Modifier" variant="outline" onClick={() => setEditing(true)}>
          <Pencil />
        </IconActionButton>
        <IconActionButton label="Rejouer" variant="outline" onClick={replayNow} disabled={replay.isPending}>
          <RotateCcw />
        </IconActionButton>
        <IconActionButton label="Supprimer" variant="destructive" onClick={remove_} disabled={remove.isPending}>
          <Trash2 />
        </IconActionButton>
      </TableCell>
    </TableRow>
  );
}

export function AssignmentRulesPage() {
  const rules = useAssignmentRules();
  const properties = useProperties();
  const categories = useCategories();

  const propertyNames = new Map(properties.data?.map((property) => [property.id, property.name]));
  const categoryLabels = new Map(categories.data?.map((category) => [category.id, category.label]));
  const { page, pageCount, start, end, setPage } = usePagination(rules.data?.length ?? 0);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Affectation</h1>
      <AssignmentTabs />

      {rules.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {rules.isError && <p className="text-destructive">Impossible de charger les règles.</p>}
      {rules.data?.length === 0 && <p className="text-muted-foreground">Aucune règle apprise pour l'instant.</p>}

      {rules.data && rules.data.length > 0 && (
        <Card className="gap-0 overflow-hidden py-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Noyau normalisé</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Fourchette</TableHead>
                <TableHead>Appliquée</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.data.slice(start, end).map((rule) => (
                <RuleRow key={rule.id} rule={rule} propertyNames={propertyNames} categoryLabels={categoryLabels} />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            pageCount={pageCount}
            start={start}
            end={end}
            total={rules.data.length}
            onPageChange={setPage}
          />
        </Card>
      )}
    </div>
  );
}
